import dayjs, { Dayjs } from 'dayjs';
import i18next from 'i18next';

import { getStoredOrDefaultCulture } from './user';
import { minutesInTherapyDay, therapyDayStartHour } from '@shared/constants/therapyDay';

const dayAndShortMonthFormat: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short' };
const dayShortMonthAndYearFormat: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short', year: 'numeric' };

const getIsWordWrappingFixNeededForCulture = (culture: string) => {
  return ['ja', 'ko', 'zh-cn'].some((x) => x == culture);
};

const getIsFormatFixNeededForCulture = (culture: string) => {
  return ['bg', 'cs', 'sk'].some((x) => x == culture);
};

const fixWordWrapping = (dateObj: Date, culture: string, formatOptions: Intl.DateTimeFormatOptions) => {
  // Add zero-width-space and word-joiner for correct word wrapping.
  return dateObj.toLocaleDateString(culture, formatOptions).replace(/\d+/g, '​$&').replace(/\D+/g, '⁠$&');
};

export const getLocalizedDayjsDate = (date: string | Date | Dayjs | undefined) => {
  if (dayjs.isDayjs(date)) {
    return date.locale(getStoredOrDefaultCulture().dayJSLocale);
  }

  return dayjs(date, undefined, true).locale(getStoredOrDefaultCulture().dayJSLocale);
};

export const getLocalizedDayOfWeek = (date: string | Date | Dayjs | undefined) => {
  return getLocalizedDayjsDate(date).format('dd');
};

/*
 * Format 'L'.
 * Example: 08/16/2018.
 */
export const getLocalizedDate = (date: string | Date | Dayjs | undefined) => {
  return getLocalizedDayjsDate(date).format('L');
};

/*
 * Format 'LL'.
 * Example: August 16, 2018.
 */
export const getLocalizedLongDate = (dateTimeIso: string) => {
  return getLocalizedDayjsDate(dateTimeIso).format('LL');
};

/*
 * Format: 'LLL'.
 * Example: August 16, 2018 8:02 PM.
 */
export const getLocalizedLongDateAndTime = (dateTimeIso: string) => {
  return `${getLocalizedDayjsDate(dateTimeIso).format('LL')} ${getLocalizedTime(dateTimeIso)}`;
};

/*
 * Example: Never, Today, a day ago, 2 days ago, a month ago, 2 months ago...
 */
export const getTimeSinceLastSeenAsString = (secondsSinceLastSeen?: number | string | null) => {
  if (secondsSinceLastSeen === null || secondsSinceLastSeen === undefined) {
    return i18next.t('UserList.LastSeenValues.Never');
  }

  const userLastSeenNumber =
    typeof secondsSinceLastSeen === 'number'
      ? secondsSinceLastSeen
      : parseInt(secondsSinceLastSeen as unknown as string);

  const lastLogInTime = dayjs().add(userLastSeenNumber * -1, 'seconds');
  const isCurrentDate = lastLogInTime.isSame(new Date(), 'day');
  return isCurrentDate ? i18next.t('UserList.LastSeenValues.Today') : lastLogInTime.fromNow();
};

export const getLocalizedNumeric = (number: number, fractionDigits?: number) => {
  const culture = getStoredOrDefaultCulture().dayJSLocale;
  return number.toLocaleString(culture, {
    useGrouping: false,
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  });
};

/*
 * Example: 20 oct 2024.
 */
export const getLocalizedDayShortMonthAndYear = (dateTimeIso: string) => {
  const dateObj = new Date(dateTimeIso);

  const culture = getStoredOrDefaultCulture().dayJSLocale;

  if (getIsWordWrappingFixNeededForCulture(culture)) {
    return fixWordWrapping(dateObj, culture, dayShortMonthAndYearFormat);
  }
  if (getIsFormatFixNeededForCulture(culture)) {
    // JS Date uses wrong month short format for these languages — numbers separated by dots instead of shortened words
    // (see https://stackoverflow.com/questions/64506088/cs-cz-locale-and-javascript-new-date-constructor-doesnt-allow-for-shorthand-dat).
    return getLocalizedLongDateWithShortMonth(dateTimeIso);
  }

  return dateObj.toLocaleDateString(culture, dayShortMonthAndYearFormat);
};

/*
 * Example: 20 sep / 20 sep 2022.
 */
const getLocalizedDayAndShortMonthImpl = (dateTimeIso: string, isYearShownIfNotCurrent: boolean) => {
  const dateObj = dayjs(dateTimeIso).toDate();

  const culture = getStoredOrDefaultCulture().dayJSLocale;

  const options: Intl.DateTimeFormatOptions =
    dateObj.getFullYear() == dayjs().year() || !isYearShownIfNotCurrent
      ? dayAndShortMonthFormat
      : dayShortMonthAndYearFormat;

  if (getIsWordWrappingFixNeededForCulture(culture)) {
    return fixWordWrapping(dateObj, culture, options);
  }
  if (getIsFormatFixNeededForCulture(culture)) {
    // JS Date uses wrong month short format for these languages — numbers separated by dots instead of shortened words
    // (see https://stackoverflow.com/questions/64506088/cs-cz-locale-and-javascript-new-date-constructor-doesnt-allow-for-shorthand-dat).
    // Dayjs has correct short month format, but doesn't have an option to show 'll'-formatted date without year.
    // Removing the current year manually from dayjs date.
    return getLocalizedLongDateWithShortMonth(dateTimeIso).replace(dayjs().year().toString(), '').trim();
  }

  return dateObj.toLocaleDateString(culture, options);
};

/*
 * Format: 'D MMM'.
 * Example: 20 sep.
 */
export const getLocalizedDayAndShortMonth = (dateTimeIso: string) => {
  const isYearShownIfNotCurrent = false;
  return getLocalizedDayAndShortMonthImpl(dateTimeIso, isYearShownIfNotCurrent);
};

/*
 * Format: 'D MMM' / 'D MMM YYYY'.
 * If current year is not equal to a year of given date, adds also a year.
 * Example: 20 sep / 20 sep 2022.
 */
export const getLocalizedDayMonthAndYearIfNotCurrent = (dateTimeIso: string) => {
  const isYearShownIfNotCurrent = true;
  return getLocalizedDayAndShortMonthImpl(dateTimeIso, isYearShownIfNotCurrent);
};

/*
 * Format: 'D MMM, LT'.
 * Example: 15 feb, 10:25.
 */
export const getLocalizedDayShortMonthAndTime = (dateTimeIso: string) => {
  return `${getLocalizedDayAndShortMonth(dateTimeIso)}, ${getLocalizedTime(dateTimeIso)}`;
};

/*
 * Example: 19:25 / 7:25 PM.
 * If dateTimeIso string includes timezone, it is adjusted according to the current timezone.
 * In opposite case time is returned as is.
 * Note: most timestamps with time coming from backend have UTC timezone (type DateTimeOffset).
 * In rare cases they have no timezone (type DateTime).
 */
export const getLocalizedTime = (dateTimeIso: string) => {
  if (!dateTimeIso) {
    return '';
  }

  const culture = getStoredOrDefaultCulture().dayJSLocale;
  const date = new Date(dateTimeIso);
  return Intl.DateTimeFormat(culture, {
    hour: 'numeric',
    minute: 'numeric',
  }).format(date);
};

/*
 * A whole hour in the clock the culture uses: 19 / 7 PM.
 */
export const getLocalizedHour = (dateTimeIso: string) => getLocalizedTime(dateTimeIso).replace(/[:.]00/, '');

const getUses12HourClock = () => {
  const culture = getStoredOrDefaultCulture().dayJSLocale;
  return Intl.DateTimeFormat(culture, { hour: 'numeric' }).resolvedOptions().hour12 === true;
};

/*
 * Returns an hour of the therapy day in the clock the culture uses.
 * The therapy day counts hours from its own start, so its ends (0 h and 24 h) both read as noon.
 * Example: 0 -> 12 PM, 4 -> 4 PM, 12 -> 12 AM.
 * For 24h cultures "00" is returned as minutes: 4 -> 04:00.
 */
export const getLocalizedTherapyDayHour = (hoursFromTherapyDayStart: number) =>
  (getUses12HourClock() ? getLocalizedHour : getLocalizedTime)(toTherapyDayTimeIso(hoursFromTherapyDayStart * 60));

/*
 * The same for a moment inside the therapy day, keeping the minutes: the start or end of a period.
 * Example: 565 -> 9:25 PM.
 */
export const getLocalizedTherapyDayTime = (minutesFromTherapyDayStart: number) =>
  getLocalizedTime(toTherapyDayTimeIso(minutesFromTherapyDayStart));

/*
 * Only the time of day matters here, so any date does - the therapy day is read as a clock, not as
 * a calendar date.
 */
const toTherapyDayTimeIso = (minutesFromTherapyDayStart: number) => {
  const minuteOfDay = (therapyDayStartHour * 60 + minutesFromTherapyDayStart) % minutesInTherapyDay;
  const pad = (value: number) => Math.floor(value).toString().padStart(2, '0');

  return `2000-01-01T${pad(minuteOfDay / 60)}:${pad(minuteOfDay % 60)}:00`;
};

/*
 * Format: 'L LT'.
 * Example: 08/16/2018 2:48 PM.
 */
export const getLocalizedDateAndTime = (dateTimeIso: string) => {
  return `${getLocalizedDate(dateTimeIso)} ${getLocalizedTime(dateTimeIso)}`;
};

/*
 * Format: 'll', 'MMM D, YYYY'.
 * Example: Aug 16, 2018.
 */
export const getLocalizedLongDateWithShortMonth = (dateTimeIso: string) => {
  return getLocalizedDayjsDate(dateTimeIso).format('ll');
};

/*
 * Format: 'lll'.
 * Example: Aug 16, 2018 8:02 PM.
 */
export const getLocalizedLongDateWithShortMonthAndTime = (dateTimeIso: string) => {
  return `${getLocalizedDayShortMonthAndYear(dateTimeIso)} ${getLocalizedTime(dateTimeIso)}`;
};

/*
 * Format: 'MMMM YYYY'.
 * Example: September 2024
 */
export const getLocalizedMonthAndYear = (date: string | Date | Dayjs) => {
  return getLocalizedDayjsDate(date).format('MMMM YYYY');
};

/*
 * Format: 'L (ddd)'.
 * Example: 08/16/2018 (Thu).
 */
export const getLocalizedDateWithWeekDay = (dateTimeIso: string) => {
  return getLocalizedDayjsDate(dateTimeIso).format('L (ddd)');
};

/*
 * Format: 'L (ddd), LT'.
 * Example: 08/16/2018 (Thu), 2:48 PM.
 */
export const getLocalizedDateWithWeekDayAndTime = (dateTimeIso: string) => {
  return `${getLocalizedDateWithWeekDay(dateTimeIso)}, ${getLocalizedTime(dateTimeIso)}`;
};

export const getLocalizedDateTimeForFile = (dateTime: string | dayjs.Dayjs) => {
  return dayjs(dateTime).format('YYYY_MM_DD_HH_mm');
};
